// src/pages/Settings.jsx - Updated with Instagram automation support
import React, { useState, useEffect  } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsAPI, instagramAutomationAPI } from '../services/api';
import CredentialsTab from '../components/settings/CredentialsTab';
import PlatformsTab from '../components/settings/PlatformsTab';
import AccountTab from '../components/settings/AccountTab';
import ContentSourcesTab from '../components/settings/ContentSourcesTab';
import EmailNotificationsTab from '../components/settings/EmailNotificationsTab';
import ImageSettingsTab from "../components/settings/ImageSettingsTab.jsx";
import SchedualTab from "../components/settings/SchedulingTab.jsx";
import TwitterOnboardingWizard from '../components/settings/TwitterOnboardingWizard';
import BlueskyOnboardingWizard from '../components/settings/BlueskyOnboardingWizard';
import LinkedInOnboardingWizard from "../components/settings/LinkedInOnboardingWizard.jsx";
import { InstagramWizardWithAutomation } from "../components/settings/InstagramAutomatedSetup.jsx";
import {
    Cog6ToothIcon,
    KeyIcon,
    ShareIcon,
    UserIcon,
    GlobeAltIcon,
    EnvelopeIcon,
    ArrowPathRoundedSquareIcon,
    ClockIcon,
    CameraIcon,
    SparklesIcon, RocketLaunchIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { OnboardingChecklist } from '../components/onboarding/OnboardingChecklist';

function Settings() {
    const [activeTab, setActiveTab] = useState('platforms');
    const [showLinkedInWizard, setShowLinkedInWizard] = useState(false);
    const [showInstagramWizard, setShowInstagramWizard] = useState(false);
    const [showTwitterWizard, setShowTwitterWizard] = useState(false);
    const [showBlueskyWizard, setShowBlueskyWizard] = useState(false);
    const queryClient = useQueryClient();

    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.substring(1); // Remove the '#'
            if (hash && tabs.some(tab => tab.id === hash)) {
                setActiveTab(hash);
            }
        };

        // Set initial tab from URL hash
        handleHashChange();

        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);


    // Get Instagram setup progress for better UX
    const { data: instagramProgress } = useQuery(
        'instagramProgress',
        instagramAutomationAPI.getProgress,
        {
            retry: 1,
            staleTime: 30000,
            refetchOnWindowFocus: false,
            onError: () => {
                // Silently fail - this is optional data
            }
        }
    );

    const handleTwitterWizardComplete = () => {
        setShowTwitterWizard(false);
        setActiveTab('platforms');
        toast.success('Twitter setup completed! You can now post to Twitter.');

        // Refresh platform settings
        queryClient.invalidateQueries('platformSettings');
        queryClient.invalidateQueries('twitterValidation');
    };

    // Handle Twitter wizard close
    const handleTwitterWizardClose = () => {
        setShowTwitterWizard(false);
    };

    // Handle Bluesky wizard completion
    const handleBlueskyWizardComplete = () => {
        setShowBlueskyWizard(false);
        setActiveTab('platforms');
        toast.success('Bluesky setup completed! You can now post to Bluesky.');

        // Refresh platform settings
        queryClient.invalidateQueries('platformSettings');
        queryClient.invalidateQueries('blueskyValidation');
    };

    // Handle Bluesky wizard close
    const handleBlueskyWizardClose = () => {
        setShowBlueskyWizard(false);
    };

    const tabs = [
        { id: 'onboarding', name: 'Setup Guide', icon: RocketLaunchIcon, description: 'Complete your account setup'},
        { id: 'platforms', name: 'Platforms', icon: ShareIcon, description: 'Configure social media platforms' },
        { id: 'schedule', name: 'Schedule', icon: ClockIcon, description: 'Schedule social media platforms' },
        { id: 'content-sources', name: 'Content Sources', icon: GlobeAltIcon, description: 'Websites for post inspiration' },
        { id: 'style', name: 'Content Style', icon: Cog6ToothIcon, description: 'Content style inspiration' },
        { id: 'emailNotifications', name: 'Email Notifications', icon: EnvelopeIcon, description: 'Email Settings' },
        { id: 'credentials', name: 'Credentials', icon: KeyIcon, description: 'API keys and tokens' },
        { id: 'account', name: 'Account', icon: UserIcon, description: 'Personal settings' },
        // { id: 'linkedin', name: 'LinkedIn Wizard', icon: ArrowPathRoundedSquareIcon, description: 'LinkedIn Onboarding Wizard' },

        // { id: 'instagram', name: 'Instagram Wizard', icon: SparklesIcon, description: 'Automated Instagram Setup (30s) or Manual Guide'},
    ];

    const getActiveTabName = () => {
        const currentTab = tabs.find(tab => tab.id === activeTab);
        return currentTab ? currentTab.name : 'Settings';
    };

    // Handle LinkedIn wizard completion
    const handleLinkedInWizardComplete = () => {
        setShowLinkedInWizard(false);
        setActiveTab('platforms');
        toast.success('LinkedIn setup completed! You can now post to LinkedIn.');

        // Refresh platform settings to show updated status
        queryClient.invalidateQueries('platformSettings');
        queryClient.invalidateQueries('linkedinValidation');
    };

    // Handle LinkedIn wizard close
    const handleLinkedInWizardClose = () => {
        setShowLinkedInWizard(false);
    };

    // Enhanced Instagram wizard completion handler
    const handleInstagramWizardComplete = (setupData) => {
        setShowInstagramWizard(false);
        setActiveTab('platforms');

        if (setupData?.selectedAccount) {
            toast.success(`Instagram setup completed! Connected @${setupData.selectedAccount.username}`);
        } else {
            toast.success('Instagram setup completed! You can now post to Instagram.');
        }

        // Invalidate all relevant cache keys for Instagram
        queryClient.invalidateQueries('instagramValidation');
        queryClient.invalidateQueries('instagramProgress');
        queryClient.invalidateQueries('platformSettings');
        queryClient.invalidateQueries('credentials');
    };

    // Handle Instagram wizard close
    const handleInstagramWizardClose = () => {
        setShowInstagramWizard(false);
    };

    // Handle tab clicks - show wizards for LinkedIn and Instagram
    const handleTabClick = (tabId) => {
        if (tabId === 'linkedin') {
            setShowLinkedInWizard(true);
        // } else if (tabId === 'instagram') {
        //     setShowInstagramWizard(true);
        } else {
            setActiveTab(tabId);
            window.history.replaceState(null, null, `#${tabId}`);
        }
    };

    // Check if any wizard is currently open
    const isWizardOpen = showLinkedInWizard || showInstagramWizard;

    // Get Instagram setup status for better tab display
    const instagramSetupComplete = instagramProgress?.data?.isComplete;
    const instagramSetupPercentage = instagramProgress?.data?.percentage || 0;

    return (
        <div className="space-y-4 sm:space-y-6 w-full min-w-0 max-w-none">
            <div className="pb-2 sm:pb-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Settings</h1>
                <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-700">
                    Manage your account, credentials, content sources, and platform connections
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
                {/* Mobile Dropdown - Enhanced Version */}
                <div className="sm:hidden relative">
                    <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-t-lg">
                        {(() => {
                            const currentTab = tabs.find(tab => tab.id === activeTab);
                            const Icon = currentTab?.icon;
                            return (
                                <>
                                    {Icon && <Icon className="h-5 w-5 text-blue-600 flex-shrink-0" />}
                                    <span className="font-medium text-gray-900">Current: {currentTab?.name || 'Settings'}</span>
                                </>
                            );
                        })()}
                    </div>
                    <select
                        value={activeTab}
                        onChange={(e) => handleTabClick(e.target.value)}
                        className="block w-full pl-3 pr-10 py-3 text-base border-0 border-t border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                        {tabs.map((tab) => (
                            <option key={tab.id} value={tab.id}>
                                {tab.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Desktop Tabs */}
                <div className="hidden sm:block">
                    <nav className="-mb-px flex space-x-0">
                        {tabs.map((tab, index) => {
                            const Icon = tab.icon;
                            const isActive = tab.id === 'linkedin' ? showLinkedInWizard :
                                tab.id === 'instagram' ? showInstagramWizard :
                                    activeTab === tab.id;

                            // Special styling for Instagram tab based on setup status
                            const instagramTabStyle = tab.id === 'instagram'
                                ? instagramSetupComplete
                                    ? 'text-green-600 hover:text-green-700'
                                    : instagramSetupPercentage > 0
                                        ? 'text-orange-600 hover:text-orange-700'
                                        : 'text-blue-600 hover:text-blue-700'
                                : '';

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabClick(tab.id)}
                                    className={`${
                                        isActive
                                            ? 'border-blue-500 text-blue-600 bg-blue-50'
                                            : tab.id === 'instagram' && instagramTabStyle
                                                ? `border-transparent ${instagramTabStyle} hover:border-gray-300 hover:bg-gray-50`
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                                    } flex-1 py-3 px-2 border-b-2 font-medium text-xs lg:text-sm flex flex-col lg:flex-row items-center justify-center lg:justify-start group relative transition-all duration-200`}
                                    title={tab.description}
                                >
                                    <Icon className="h-4 w-4 lg:h-5 lg:w-5 lg:mr-2 flex-shrink-0 mb-1 lg:mb-0" />
                                    <span className="truncate text-center lg:text-left leading-tight">
                            {tab.name.length > 12 ? tab.name.replace(/\s+/g, ' ').substring(0, 20) + '...' : tab.name}
                        </span>

                                    {/* Instagram setup progress indicator */}
                                    {tab.id === 'instagram' && instagramSetupPercentage > 0 && instagramSetupPercentage < 100 && (
                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full flex items-center justify-center">
                                            <span className="text-xs text-white font-bold">!</span>
                                        </div>
                                    )}

                                    {/* Instagram setup complete indicator */}
                                    {tab.id === 'instagram' && instagramSetupComplete && (
                                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                                            <span className="text-xs text-white font-bold">✓</span>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Tab Content */}
            <div className="mt-4 sm:mt-6 w-full min-w-0 flex-1">
                {!isWizardOpen && (
                    <>

                        {activeTab === 'onboarding' && <OnboardingChecklist />}
                        {activeTab === 'platforms' && <PlatformsTab />}
                        {activeTab === 'schedule' && <SchedualTab />}
                        {activeTab === 'content-sources' && <ContentSourcesTab />}
                        {activeTab === 'style' && <ImageSettingsTab />}
                        {activeTab === 'emailNotifications' && <EmailNotificationsTab />}
                        {activeTab === 'credentials' && <CredentialsTab />}
                        {activeTab === 'account' && <AccountTab />}

                    </>
                )}

                {/* Show wizard information when no wizard is open but wizard tab is selected */}
                {!isWizardOpen && (activeTab === 'instagram') && (
                    <div className="text-center py-8">
                        <div className="text-6xl mb-4">
                            {activeTab === 'linkedin' ? '💼' : '🤖'}
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {activeTab === 'linkedin' ? 'LinkedIn' : 'Instagram'} Wizard
                        </h3>
                        <div className="space-y-4">
                            <p className="text-gray-600 mb-4">
                                Choose between automated setup (30 seconds) or guided manual setup.
                            </p>

                            {/* Instagram setup status */}
                            {instagramSetupComplete ? (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto">
                                    <div className="flex items-center">
                                        <div className="text-2xl mr-3">✅</div>
                                        <div>
                                            <h4 className="font-medium text-green-800">Setup Complete</h4>
                                            <p className="text-sm text-green-600">
                                                Your Instagram account is connected and ready to use.
                                            </p>
                                        </div>
                                    </div>
                                    {instagramProgress?.data?.accountInfo && (
                                        <p className="text-xs text-green-600 mt-2">
                                            Connected: @{instagramProgress.data.accountInfo.username}
                                        </p>
                                    )}
                                </div>
                            ) : instagramSetupPercentage > 0 ? (
                                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 max-w-md mx-auto">
                                    <div className="flex items-center">
                                        <div className="text-2xl mr-3">⚠️</div>
                                        <div>
                                            <h4 className="font-medium text-orange-800">Setup Incomplete</h4>
                                            <p className="text-sm text-orange-600">
                                                Setup is {instagramSetupPercentage}% complete. Continue to finish.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                                    <div className="text-sm text-blue-700 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span>🤖 Automated Setup</span>
                                            <span className="text-green-600 font-medium">~30 seconds</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span>📋 Manual Setup</span>
                                            <span className="text-orange-600 font-medium">~5-10 minutes</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={() => handleTabClick(activeTab)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                                {instagramSetupComplete ? 'Reconfigure Instagram' : 'Open Instagram Wizard'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/*/!* LinkedIn Wizard Modal *!/*/}
            {/*{showLinkedInWizard && (*/}
            {/*    <LinkedInOnboardingWizard*/}
            {/*        onComplete={handleLinkedInWizardComplete}*/}
            {/*        onClose={handleLinkedInWizardClose}*/}
            {/*    />*/}
            {/*)}*/}

            {/* Instagram Wizard Modal - Now using the automation-enhanced version */}
            {showInstagramWizard && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end sm:items-center justify-center min-h-screen p-0 sm:p-4 text-center">
                        {/* Background overlay */}
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleInstagramWizardClose}></div>

                        {/* Modal panel - mobile optimized */}
                        <div className="inline-block align-bottom bg-white rounded-t-lg sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full max-h-screen sm:max-h-[90vh] overflow-y-auto">
                            {/* Header with close button */}
                            <div className="bg-gray-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 z-10">
                                <h1 className="text-base sm:text-lg font-medium text-gray-900">Instagram Setup Wizard</h1>
                                <button
                                    onClick={handleInstagramWizardClose}
                                    className="text-gray-400 hover:text-gray-600 text-2xl font-light p-2 -m-2 touch-manipulation"
                                >
                                    ✕
                                </button>
                            </div>

                            {/* Content */}
                            <div className="px-4 sm:px-6 py-4 sm:py-6">
                                <InstagramWizardWithAutomation
                                    onComplete={handleInstagramWizardComplete}
                                    onClose={handleInstagramWizardClose}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}



            {/* Enhanced Platform Setup Tips */}
            {!isWizardOpen && activeTab === 'platforms' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mt-4 sm:mt-6 -mx-2 sm:mx-0">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <span className="text-blue-400 text-lg">💡</span>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">Setup Wizards Available</h3>
                            <div className="mt-2 text-sm text-blue-700 space-y-1">
                                <p>• Use the <strong>LinkedIn Wizard</strong> for guided LinkedIn integration setup</p>
                                {/*<p>• Use the <strong>Instagram Wizard</strong> for automated (30s) or manual (5-10min) setup</p>*/}
                                {/*<p>• Instagram automation can discover your accounts automatically from just an access token</p>*/}
                                {/*<p>• Both wizards will walk you through the complete setup process</p>*/}
                                <p>• Tha wizard will walk you through the complete setup process</p>
                                <p>• Manual setup is also available through the Credentials tab</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Twitter Wizard Modal */}
            {showTwitterWizard && (
                <TwitterOnboardingWizard
                    onComplete={handleTwitterWizardComplete}
                    onClose={handleTwitterWizardClose}
                />
            )}

            {/* Bluesky Wizard Modal */}
            {showBlueskyWizard && (
                <BlueskyOnboardingWizard
                    onComplete={handleBlueskyWizardComplete}
                    onClose={handleBlueskyWizardClose}
                />
            )}

            {/* Instagram-specific tips when Instagram tab is active */}
            {!isWizardOpen && (activeTab === 'instagram' || (activeTab === 'platforms' && !instagramSetupComplete)) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <span className="text-green-400 text-lg">🤖</span>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-green-800">Instagram Automation Features</h3>
                            <div className="mt-2 text-sm text-green-700 space-y-1">
                                <p>• <strong>Automated Discovery:</strong> Finds all your Instagram Business accounts automatically</p>
                                <p>• <strong>Smart Selection:</strong> Auto-selects the best account or lets you choose</p>
                                <p>• <strong>One-Click Setup:</strong> Just paste your access token and we handle the rest</p>
                                <p>• <strong>Health Monitoring:</strong> Automatic token expiry checks and renewal reminders</p>
                                <p>• <strong>Fallback Support:</strong> Manual setup available if automation doesn't work</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Settings;